'use client'

import { motion } from "framer-motion"
import { useState, useRef } from "react"
import { ChevronLeft, ChevronRight } from 'lucide-react'

const services = [
  {
    title: "Taxcloud",
    description: "Products that perform seamlessly during any kind of surge, so you don't have to worry about uptime and reliability.",
    image: "./assets/heroSection.jpeg",
  },
  {
    title: "Enterprise",
    description: "Products that perform seamlessly during any kind of surge, so you don't have to worry about uptime and reliability.",
    image: "./assets/service2.jpeg",
  },
  {
    title: "GST Software",
    description: "Products that perform seamlessly during any kind of surge, so you don't have to worry about uptime and reliability.",
    image: "./assets/service3.jpeg",
  },
  {
    title: "Tax Planning",
    description: "Strategic tax planning solutions to help optimize your tax position and ensure compliance with regulations.",
    image: "./assets/service4.jpeg",
  },
  {
    title: "Audit Support",
    description: "Comprehensive audit support services to help you navigate through tax audits with confidence.",
    image: "./assets/service2.jpeg",
  },
]

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
}

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
}

export default function Services() {
  const [activeSlide, setActiveSlide] = useState(0)
  const scrollContainerRef = useRef(null)

  const scrollTo = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth
      const newScrollPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount
      
      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      })
    }
  }

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollPercentage = (container.scrollLeft / (container.scrollWidth - container.clientWidth)) * (services.length - 1)
      setActiveSlide(Math.round(scrollPercentage))
    }
  }

  return (
    <div className="min-h-screen  py-16 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto text-center mb-12"
      >
        <h2 className="text-4xl font-bold text-cyan-700 mb-8">Our Services</h2>
      </motion.div>

      <div className="max-w-7xl mx-auto relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto gap-8 pb-8 snap-x snap-mandatory scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            borderRadius:'10px'
          }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={cardVariants}
              className="flex-none w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)] snap-center"
              
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all duration-300 hover:-translate-y-2" style={{borderRadius:'10px'}}>
                <div className="relative h-48 w-full">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold  mb-3 text-cyan-700">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-full flex items-center gap-2 hover:bg-blue-700 transition-colors duration-300">
                    KNOW MORE
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Navigation Buttons */}
        <div className="hidden md:block">
          <button
            onClick={() => scrollTo('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 bg-white/90 backdrop-blur-sm text-blue-600 p-2 rounded-full shadow-lg hover:bg-white transition-colors duration-300"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() => scrollTo('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 bg-white/90 backdrop-blur-sm text-blue-600 p-2 rounded-full shadow-lg hover:bg-white transition-colors duration-300"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Indicators */}
        <div className="flex justify-center items-center space-x-2 mt-8">
          {services.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (scrollContainerRef.current) {
                  const container = scrollContainerRef.current
                  const scrollAmount = (container.scrollWidth - container.clientWidth) * (index / (services.length - 1))
                  container.scrollTo({
                    left: scrollAmount,
                    behavior: 'smooth'
                  })
                }
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                activeSlide === index ? 'w-8 bg-blue-600' : 'bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

